// src/components/dashboard/tabs/DashboardMainTab.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    IconButton,
    TextField,
    Typography,
    Avatar,
    Chip,
} from '@mui/material';
import {
    Rocket,
    Book,
    Trophy,
    Trash2,
    Plus,
    Edit3,
    Award,
    Star,
    Clock,
    CheckCircle2
} from 'lucide-react';
import {
    updateUserProfile,
    startCamundaProcess,
    fetchInstanceByProcessId,
    updateInstanceArchitecture,
    createSandboxComponent,
    deployTemplate,
    fetchSubscriptionStatus
} from '../../../services/api';
import SubscriptionStatus from '../../../components/subscription/SubscriptionStatus';
import { useError } from '../../../contexts/ErrorContext';
import { useDialog } from '../../../hooks/useDialog';
import FormDialog from '../../../components/common/FormDialog';
import CreateSandboxDialog from '../../sandbox/CreateSandboxDialog';
import GenerationLoader from '../../GenerationLoader';
import config from '../../../config';

const DashboardMainTab = ({
                              userInfo,
                              token,
                              profile,
                              achievements,
                              badges,
                              mySandboxes,
                              enrollments,
                              componentTypes,
                              setMySandboxes
                          }) => {
    const { t } = useTranslation();
    const { showError } = useError();

    // Используем хуки для управления диалогами
    const editProfileDialog = useDialog();
    const createSandboxDialog = useDialog();
    const deleteSandboxDialog = useDialog();

    // State для формы и процесса
    const [sandboxToDelete, setSandboxToDelete] = useState(null);
    const [dialogError, setDialogError] = useState(null);

    // State для формы редактирования профиля
    const [formData, setFormData] = useState({
        name: profile?.name || '',
        last_name: profile?.last_name || '',
        bio: profile?.bio || '',
    });

    // State для процесса генерации
    const [processId, setProcessId] = useState(null);
    const [sandboxId, setSandboxId] = useState(null);
    const [generationStarted, setGenerationStarted] = useState(false);

    // State для статуса подписки
    const [subscriptionStatus, setSubscriptionStatus] = useState(null);

    // Загружаем статус подписки
    useEffect(() => {
        loadSubscriptionStatus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const loadSubscriptionStatus = async () => {
        try {
            const status = await fetchSubscriptionStatus(token);
            setSubscriptionStatus(status);
        } catch (error) {
            // Error loading subscription status
        }
    };

    // Обработчики для песочниц
    const handleOpenCreateDialog = () => {
        // Проверяем, может ли пользователь создать песочницу
        if (subscriptionStatus?.can_create && !subscriptionStatus.can_create.sandbox) {
            showError(t('subscription.error.sandbox_limit_reached', {
                current: subscriptionStatus.limits?.sandboxes?.used || 0,
                max: subscriptionStatus.limits?.sandboxes?.max || 0
            }));
            return;
        }

        createSandboxDialog.openDialog();
    };

    const handleCreateSandbox = async (data) => {
        try {
            // Проверяем лимиты еще раз перед созданием
            const currentStatus = await fetchSubscriptionStatus(token);
            if (!currentStatus.can_create.sandbox) {
                showError(t('subscription.error.sandbox_limit_reached', {
                    current: currentStatus.limits.sandboxes.used,
                    max: currentStatus.limits.sandboxes.max
                }));
                return;
            }
            switch (data.type) {
                case 0: // По описанию
                    const response = await startCamundaProcess(
                        token,
                        data.prompt,
                        userInfo.id,
                        'base',
                        null,
                        data.ai_tutor_id,
                        {
                            onError: (error) => {
                                // Проверяем, не связана ли ошибка с лимитами подписки
                                if (error.code === 'subscription_limit_reached') {
                                    showError(t('subscription.error.sandbox_limit_reached', {
                                        current: error.usage?.current,
                                        max: error.usage?.limit
                                    }));
                                } else {
                                    showError(error);
                                }
                            }
                        }
                    );
                    setProcessId(response.process_id);
                    setSandboxId(response.sandbox_id);

                    // Обновляем статус подписки после создания
                    loadSubscriptionStatus();
                    break;

                case 1: // Вручную
                    // Запускаем процесс в ручном режиме
                    const manualResponse = await startCamundaProcess(
                        token,
                        'manual',
                        userInfo.id,
                        'manual',
                        null,
                        data.ai_tutor_id,
                        {
                            onError: (error) => {
                                // Проверяем, не связана ли ошибка с лимитами подписки
                                if (error.code === 'subscription_limit_reached') {
                                    showError(t('subscription.error.sandbox_limit_reached', {
                                        current: error.usage?.current,
                                        max: error.usage?.limit
                                    }));
                                } else {
                                    showError(error);
                                }
                            }
                        }
                    );

                    // Создаем архитектуру из выбранных компонентов
                    const architecture = {
                        backend_components: data.components.map(component => ({
                            name: component.name,
                            description: component.prompt,
                            type: (componentTypes || []).find(t => t.id === component.type)?.name || '',
                            subtype: component.subtype
                        }))
                    };

                    // Сохраняем архитектуру в instance
                    const instance = await fetchInstanceByProcessId(token, manualResponse.process_id, {
                        onError: (error) => showError(error)
                    });

                    await updateInstanceArchitecture(token, instance.id, architecture, {
                        onError: (error) => showError(error)
                    });

                    // Создаем компоненты
                    for (const component of data.components) {
                        await createSandboxComponent(token, {
                            sandbox: manualResponse.sandbox_id,
                            name: component.systemName,
                            host: `${config.sandboxUrl}/${manualResponse.namespace}/${component.systemName}`,
                            port: 0,
                            type_name_input: (componentTypes || []).find(t => t.id === component.type)?.name,
                            status: 'creating',
                            configuration: {
                                name: component.systemName,
                                description: component.prompt,
                                type: (componentTypes || []).find(t => t.id === component.type)?.name,
                                subtype: component.subtype
                            },
                            description: component.prompt
                        }, {
                            onError: (error) => showError(error)
                        });
                    }

                    setProcessId(manualResponse.process_id);
                    setSandboxId(manualResponse.sandbox_id);
                    break;

                case 2: // Из шаблона
                    const templateResponse = await deployTemplate(
                        token,
                        data.templateId,
                        {
                            onError: (error) => showError(error)
                        }
                    );
                    setProcessId(templateResponse.process_id);
                    setSandboxId(templateResponse.sandbox_id);
                    break;

                case 3: // Мне повезёт
                    const luckyResponse = await startCamundaProcess(
                        token,
                        t('create_sandbox.lucky.prompt'),
                        userInfo.id,
                        'base',
                        null,
                        data.ai_tutor_id,
                        {
                            onError: (error) => showError(error)
                        }
                    );
                    setProcessId(luckyResponse.process_id);
                    setSandboxId(luckyResponse.sandbox_id);
                    break;

                default:
                    showError(t('dashboard.error.unknown_sandbox_type'));
                    return;
            }

            setGenerationStarted(true);
            createSandboxDialog.closeDialog();

            // Обновляем список песочниц после создания
            setTimeout(() => {
                loadSubscriptionStatus();
                if (setMySandboxes) {
                    // Если есть функция обновления списка песочниц, вызываем ее
                    const fetchSandboxes = async () => {
                        try {
                            // Здесь должен быть код для получения обновленного списка песочниц
                            // Например, вызов API или другой способ обновления
                            // Для примера просто добавляем новую песочницу в список
                            if (sandboxId && mySandboxes) {
                                const newSandbox = {
                                    id: sandboxId,
                                    name: data.prompt.substring(0, 30) + '...',
                                    created_at: new Date().toISOString()
                                };
                                setMySandboxes([...mySandboxes, newSandbox]);
                            }
                        } catch (error) {
                            // Error fetching sandboxes
                        }
                    };
                    fetchSandboxes();
                }
            }, 1000);
        } catch (err) {
            if (err.response?.data?.code === 'subscription_limit_reached') {
                showError(t('subscription.error.sandbox_limit_reached', {
                    current: err.response.data.usage?.current,
                    max: err.response.data.usage?.limit
                }));
            } else if (err.code === 'subscription_limit_reached') {
                showError(t('subscription.error.sandbox_limit_reached', {
                    current: err.usage?.current,
                    max: err.usage?.limit
                }));
            } else {
                showError(t('dashboard.error.creating_sandbox'));
            }
        }
    };

    const handleDeleteClick = (sandbox) => {
        setSandboxToDelete(sandbox);
        deleteSandboxDialog.openDialog();
    };

    const handleDeleteConfirm = async () => {
        if (!sandboxToDelete) return;

        try {
            await startCamundaProcess(
                token,
                'delete',
                userInfo.id,
                'delete',
                sandboxToDelete.id,
                null,
                {
                    onError: (error) => showError(error)
                }
            );

            // Обновляем список песочниц
            setMySandboxes(prev => prev.filter(s => s.id !== sandboxToDelete.id));
            deleteSandboxDialog.closeDialog();
            setSandboxToDelete(null);
        } catch (err) {
            showError(t('dashboard.error.deleting_sandbox'));
        }
    };

    // Обработчик для редактирования профиля
    const handleSaveProfile = async () => {
        try {
            await updateUserProfile(token, profile.id, formData, {
                onError: (error) => showError(error)
            });
            editProfileDialog.closeDialog();
            // Обновляем данные профиля в родительском компоненте будет происходить при следующем рендере
        } catch (err) {
            showError(t('dashboard.error.saving_profile'));
        }
    };

    return (
        <div className="space-y-8">
            {/* Профиль */}
            <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
                <div className="grid grid-cols-12 gap-4">
                    {/* Основная информация о пользователе */}
                    <div className="col-span-5">
                        <div className="flex gap-4">
                            <Avatar className="bg-[#FF6F00] mr-2">
                                {profile?.avatar
                                    ? profile.avatar
                                    : userInfo.username.charAt(0).toUpperCase()}
                            </Avatar>
                            <div>
                                <div className="flex items-center gap-3">
                                    <h1 className="text-2xl font-bold">
                                        {profile?.name
                                            ? `${profile.name} ${profile.last_name}`
                                            : userInfo.username}
                                    </h1>
                                    <IconButton onClick={() => editProfileDialog.openDialog()} size="small">
                                        <Edit3 className="w-4 h-4" />
                                    </IconButton>
                                </div>
                                <p className="text-gray-600 mt-1">{profile?.bio || t('dashboard.profile.add_bio')}</p>
                                <div className="flex gap-4 mt-4">
                                    <div className="flex items-center gap-2">
                                        <Trophy className="w-5 h-5 text-yellow-500" />
                                        <span>{t('dashboard.profile.level')} {profile?.level || 1}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Star className="w-5 h-5 text-purple-500" />
                                        <span>{profile?.points || 0} {t('dashboard.profile.points')}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Информация о подписке */}
                    <div className="col-span-4">
                        <SubscriptionStatus token={token} onError={showError} />
                    </div>

                    {/* Достижения и бейджи */}
                    <div className="col-span-3 flex flex-col items-end gap-4">
                        {achievements?.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                                <Typography variant="subtitle2" className="w-full text-gray-600 text-right">
                                    {t('dashboard.profile.achievements')}
                                </Typography>
                                {(achievements || []).slice(0, 3).map(achievement => (
                                    <Chip
                                        key={achievement.id}
                                        label={achievement.achievement?.name || ''}
                                        icon={<Trophy className="w-4 h-4" />}
                                        size="small"
                                        className="bg-yellow-50"
                                    />
                                ))}
                                {achievements.length > 3 && (
                                    <Chip
                                        label={`+${achievements.length - 3}`}
                                        size="small"
                                        className="bg-yellow-50"
                                    />
                                )}
                            </div>
                        )}

                        {badges?.length > 0 && (
                            <div className="flex gap-2">
                                {(badges || []).slice(0, 5).map(badge => (
                                    <Award
                                        key={badge.id}
                                        className="w-6 h-6 text-blue-500"
                                        title={badge.badge?.name}
                                    />
                                ))}
                                {badges.length > 5 && (
                                    <div className="text-sm text-gray-500">+{badges.length - 5}</div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Основные секции */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Секция песочниц */}
                <section className="bg-white rounded-xl shadow-sm p-6">
                    <div className="flex items-center gap-2 mb-6">
                        <Rocket className="w-6 h-6 text-blue-500" />
                        <h2 className="text-xl font-semibold">{t('dashboard.my_sandboxes')}</h2>
                    </div>
                    <div className="grid grid-cols-1 gap-4">
                                                <button
                            onClick={handleOpenCreateDialog}
                            className="bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 p-4 hover:border-blue-500 hover:bg-gray-100 transition-colors flex flex-col items-center justify-center gap-2"
                            disabled={subscriptionStatus?.can_create && !subscriptionStatus.can_create.sandbox}
                        >
                            <Plus className="w-8 h-8 text-gray-400" />
                            <span className="text-gray-600">
                                {subscriptionStatus?.can_create && !subscriptionStatus.can_create.sandbox
                                    ? t('dashboard.sandbox_limit_reached')
                                    : t('dashboard.to_create_sandbox')}
                            </span>
                            {subscriptionStatus?.can_create && !subscriptionStatus.can_create.sandbox && (
                                <span className="text-xs text-gray-500">
                                    {t('subscription.used_of_limit', {
                                        used: subscriptionStatus.limits?.sandboxes?.used || 0,
                                        limit: subscriptionStatus.limits?.sandboxes?.max || 0
                                    })}
                                </span>
                            )}
                        </button>
                        {(mySandboxes || []).map(sandbox => (
                            <div key={sandbox.id} className="bg-white rounded-lg shadow-sm p-4 border border-gray-100 hover:border-gray-200">
                                <div className="flex justify-between items-start">
                                    <Link
                                        to={`/sandbox/${sandbox.id}`}
                                        className="text-lg font-medium text-blue-600 hover:text-blue-800"
                                    >
                                        {sandbox.name}
                                    </Link>
                                    <IconButton
                                        onClick={() => handleDeleteClick(sandbox)}
                                        className="text-red-500 hover:text-red-700"
                                        size="small"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </IconButton>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Секция курсов */}
                <section className="bg-white rounded-xl shadow-sm p-6">
                    <div className="flex items-center gap-2 mb-6">
                        <Book className="w-6 h-6 text-green-500" />
                        <h2 className="text-xl font-semibold">{t('dashboard.my_courses')}</h2>
                    </div>
                    <div className="space-y-4">
                        {(enrollments || []).length > 0 ? (
                            (enrollments || [])
                                .filter(enrollment => enrollment.is_active)
                                .map(enrollment => (
                                    <div key={enrollment.id} className="bg-white rounded-lg shadow-sm p-4 border border-gray-100 hover:border-gray-200">
                                        <div className="flex justify-between items-start">
                                            <div>
                                                <Link
                                                    to={`/courses/${enrollment.course.id}`}
                                                    className="text-lg font-medium text-blue-600 hover:text-blue-800"
                                                >
                                                    {enrollment.course.title}
                                                </Link>
                                                {enrollment.course.description && (
                                                    <p className="text-sm text-gray-600 mt-1">
                                                        {enrollment.course.description.length > 80
                                                            ? `${enrollment.course.description.substring(0, 80)}...`
                                                            : enrollment.course.description}
                                                    </p>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-2">
                                                {enrollment.progress >= 100 ? (
                                                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                                                ) : (
                                                    <Clock className="w-5 h-5 text-blue-500" />
                                                )}
                                            </div>
                                        </div>
                                        <div className="mt-4">
                                            <div className="w-full bg-gray-200 rounded-full h-2">
                                                <div
                                                    className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                                                    style={{ width: `${enrollment.progress || 0}%` }}
                                                />
                                            </div>
                                            <span className="text-sm text-gray-600 mt-1">
                        {t('dashboard.progress')} {Math.round(enrollment.progress || 0)}%
                      </span>
                                        </div>
                                    </div>
                                ))
                        ) : (
                            <div className="text-center py-6 text-gray-500">
                                <p>{t('dashboard.no_active_courses')}</p>
                                <Link
                                    to="/courses"
                                    className="mt-2 inline-block text-blue-600 hover:text-blue-800"
                                >
                                    {t('dashboard.browse_courses')}
                                </Link>
                            </div>
                        )}
                    </div>
                </section>
            </div>

            {/* Диалог редактирования профиля */}
            <FormDialog
                open={editProfileDialog.isOpen}
                onClose={editProfileDialog.closeDialog}
                title={t('dashboard.edit_profile')}
                submitLabel={t('dashboard.form.save')}
                cancelLabel={t('dashboard.form.cancel')}
                onSubmit={handleSaveProfile}
            >
                <div className="space-y-4 mt-2">
                    <TextField
                        fullWidth
                        label={t('dashboard.form.first_name')}
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                    />
                    <TextField
                        fullWidth
                        label={t('dashboard.form.last_name')}
                        value={formData.last_name}
                        onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                    />
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label={t('dashboard.form.bio')}
                        value={formData.bio}
                        onChange={(e) => setFormData({...formData, bio: e.target.value})}
                    />
                </div>
            </FormDialog>

            {/* Диалог подтверждения удаления */}
            <FormDialog
                open={deleteSandboxDialog.isOpen}
                onClose={deleteSandboxDialog.closeDialog}
                title={t('dashboard.delete_sandbox.title')}
                submitLabel={t('dashboard.delete_sandbox.delete')}
                cancelLabel={t('dashboard.delete_sandbox.cancel')}
                onSubmit={handleDeleteConfirm}
                maxWidth="xs"
            >
                <Typography>
                    {t('dashboard.delete_sandbox.confirm_message', { name: sandboxToDelete?.name })}
                </Typography>
            </FormDialog>

            {/* Диалог создания песочницы */}
            <CreateSandboxDialog
                open={createSandboxDialog.isOpen}
                onClose={() => {
                    setDialogError(null);
                    createSandboxDialog.closeDialog();
                }}
                onSubmit={handleCreateSandbox}
                token={token}
                error={dialogError}
                setError={setDialogError}
                componentTypes={componentTypes}
            />

            {/* Если началась генерация, открываем лоадер */}
            {generationStarted && processId && sandboxId && (
                <GenerationLoader
                    token={token}
                    processId={processId}
                    sandboxId={sandboxId}
                />
            )}
        </div>
    );
};

export default DashboardMainTab;
